
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMediaQuery } from "@/hooks/use-mobile";
import { NavbarLogo } from './NavbarLogo';
import { NavbarDropdowns } from './NavbarDropdowns';
import { NavbarActions } from './NavbarActions';
import { MobileNavigation } from './MobileNavigation';

const Navbar: React.FC = () => {
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Check if currently on the public-facing website or admin dashboard
  const isPublicSite = !location.pathname.includes('Blog-Page-Builder') && 
                       !location.pathname.includes('variables') &&
                       !location.pathname.includes('templates') &&
                       !location.pathname.includes('posts') &&
                       !location.pathname.includes('create-post') &&
                       !location.pathname.includes('photo-album') &&
                       !location.pathname.includes('admin-gallery');
  
  // Check if on homepage specifically
  const isHomePage = location.pathname === '/home';

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleGetQuote = () => {
    if (location.pathname === '/home') {
      document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/contact';
    }
  };

  const publicNavItems = [
    {
      section: 'Services',
      items: [
        { name: 'Residential Roofing', path: '/residential-roofing' },
        { name: 'Commercial Roofing', path: '/commercial-roofing' },
        { name: 'Church Steeple Repair', path: '/specialty-services/church-steeple-repair' },
        { name: 'Roof Coatings', path: '/specialty-services/roof-coating' }
      ]
    },
    {
      section: 'Tools & Actions',
      items: [
        { name: 'INSTANT Estimate', path: 'https://carolinatrophyroofs.sky-quote.com/Instant-Estimate', external: true },
        { name: 'Call Us 24/7', path: 'tel:+12523022003', external: true },
        { name: 'Book Inspection', path: '/book-inspection' },
        { name: 'Design Your Roof', path: '/design-your-roof' },
        { name: 'Contact Us', path: '/contact' }
      ]
    },
    {
      section: 'Information',
      items: [
        { name: 'Gallery', path: '/gallery/' },
        { name: 'Resources', path: '/resources' },
        { name: 'FAQ', path: '/frequently-asked-questions' },
        { name: 'Roof Life Expectancy Report', path: '/roof-life-expectancy-report' },
        { name: 'Blog', path: '/blog' }
      ]
    },
    {
      section: 'Company',
      items: [
        { name: 'Careers', path: '/careers' },
        { name: 'Apply For Financing', path: '/financing' }
      ]
    }
  ];

  const adminNavItems = [
    { name: 'Photo Album', path: '/photo-album/' },
    { name: 'Posts', path: '/posts' },
    { name: 'Create Post', path: '/create-post' },
    { name: 'View Website', path: '/' }
  ];

  const navItems = isPublicSite ? publicNavItems : adminNavItems;

  return (
    <>
      <nav className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-300
        ${isHomePage 
          ? 'bg-black/20 backdrop-blur-xl border-b border-white/10' 
          : 'bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-lg'
        }
      `}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">{/* Increased height for premium feel */}
            
            {/* Logo Section */}
            <NavbarLogo isPublicSite={isPublicSite} isHomePage={isHomePage} />

            {/* Desktop Navigation */}
            {!isMobile && isPublicSite && (
              <NavbarDropdowns isHomePage={isHomePage} />
            )}

            {/* Admin Desktop Navigation */}
            {!isMobile && !isPublicSite && (
              <div className="hidden lg:flex items-center space-x-1">
                {adminNavItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105
                      text-gray-700 hover:text-brand-trophy-gold hover:bg-yellow-50
                      ${location.pathname === item.path ? 'bg-yellow-100 text-brand-trophy-gold' : ''}
                    `}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}

            {/* Right Side Actions */}
            <NavbarActions
              isPublicSite={isPublicSite}
              isHomePage={isHomePage}
              isMobile={isMobile}
              mobileMenuOpen={mobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
              onGetQuote={handleGetQuote}
            />
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <MobileNavigation
        isOpen={isMobile && mobileMenuOpen}
        navItems={navItems}
        currentPath={location.pathname}
        isPublicSite={isPublicSite}
        onClose={toggleMobileMenu}
        onGetQuote={handleGetQuote}
      />

      {/* Spacer to prevent content from being hidden behind fixed navbar */}
      <div className="h-20 lg:h-24" />{/* Updated spacer to match new navbar height */}
    </>
  );
};

export default Navbar;
